import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Compass, MoveRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  getRedirectPathForUnmatched,
  getModuleByPath,
} from '../../config/modules.config';
import { useModuleName } from '../../hooks/useModuleName';
import { PAGE_TITLE } from '../../styles/typography';

/** Seconds the friendly 404 lingers before auto-redirecting. */
const REDIRECT_DELAY_SECONDS = 5;

interface NotFoundPageProps {
  /**
   * Override the auto-redirect target. When omitted, the target is derived from
   * the current path: the matched module's root, or the app root (`/`) when no
   * module is identifiable.
   */
  redirectTo?: string;
  /**
   * `true` (default) for the global catch-all, which renders outside the app
   * shell and fills the viewport (`min-h-screen`). Pass `false` when rendering
   * inside `MainLayout`'s content area (e.g. a detail page whose `:id` failed
   * validation) so it fits the available space instead of forcing a nested
   * full-viewport scroll region.
   */
  standalone?: boolean;
}

/**
 * Friendly, app-wide handler for invalid / unmatched routes.
 *
 * Renders a "page not found" message and auto-redirects after a short,
 * cancellable delay: to the module root when the path's leading segment matches
 * a known module ({@link getRedirectPathForUnmatched}), otherwise to the app
 * root. A "go now" button lets the user skip the countdown.
 *
 * Used as the global `path="*"` catch-all in `App.tsx`, and rendered directly by
 * detail pages whose route param fails UUID validation (see
 * {@link useValidatedRouteId}) so a structurally invalid id never triggers a
 * 400/404 API call.
 */
export function NotFoundPage({ redirectTo, standalone = true }: NotFoundPageProps) {
  const { t } = useTranslation('common');
  const getModuleName = useModuleName();
  const location = useLocation();
  const navigate = useNavigate();

  const target = redirectTo ?? getRedirectPathForUnmatched(location.pathname);
  const isModuleTarget = target !== '/';
  const targetModule = isModuleTarget ? getModuleByPath(target) : undefined;
  const targetLabel = targetModule ? getModuleName(targetModule) : t('notFound.home');

  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS);

  useEffect(() => {
    setSecondsLeft(REDIRECT_DELAY_SECONDS);
    // Floor at 1 so the label never flashes "0s" in the moment before the
    // redirect timeout (below) fires at the same wall-clock tick.
    const interval = setInterval(() => {
      setSecondsLeft((s) => Math.max(1, s - 1));
    }, 1000);
    const timeout = setTimeout(() => {
      navigate(target, { replace: true });
    }, REDIRECT_DELAY_SECONDS * 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [navigate, target]);

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center bg-background px-4 py-12 text-center',
        standalone ? 'min-h-screen' : 'min-h-[60vh] flex-1'
      )}
    >
      <div className="flex w-full max-w-md flex-col items-center">
        <div className="relative mb-6 flex items-center justify-center">
          <span
            aria-hidden
            className="select-none text-[7rem] font-bold leading-none tracking-tight text-muted-foreground/10 sm:text-[9rem]"
          >
            404
          </span>
          <Compass className="absolute h-12 w-12 text-muted-foreground/60 sm:h-14 sm:w-14" />
        </div>

        <h1 className={`${PAGE_TITLE} mb-2`}>
          {t('notFound.title')}
        </h1>
        <p className="mb-4 max-w-sm text-sm text-muted-foreground">
          {t('notFound.message')}
        </p>

        <code className="mb-6 max-w-full truncate rounded-md border border-border bg-muted px-3 py-1.5 text-xs text-muted-foreground">
          {location.pathname}
        </code>

        <Button
          onClick={() => navigate(target, { replace: true })}
          className="w-full max-w-xs sm:w-auto"
        >
          {t('notFound.goTo', { target: targetLabel })}
          <MoveRight className="ms-2 h-4 w-4 rtl:rotate-180" />
        </Button>

        <p className="mt-4 text-xs text-muted-foreground" aria-live="polite">
          {t('notFound.redirecting', { seconds: secondsLeft, target: targetLabel })}
        </p>
      </div>
    </div>
  );
}
